// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import includes from 'lodash/includes';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import set from 'lodash/set';

import appConfig from '@nerdwallet/app-config';

import datadog from '@nerdwallet/datadog';
import {
  NOTFOUND_ERROR_MSG,
  PREQUAL_ISSUER_NOTFOUND_ERROR_MSG,
} from '../lib/errors';
import fetchPrequalRedirectData from '../lib/fetch-prequal-redirect-data';
import fetchRedirectData from '../lib/fetch-redirect-data';
import fetchUuidRedirectData from '../lib/fetch-uuid-redirect-data';
import investingOfferIdMapping from '../config/investing-providers-in-marketplace';
import PREQUAL_SERVICE_ISSUERS from '../config/prequal-service-issuers';
import REDIRECT_TIMEOUT from '../lib/redirect-utils';
// eslint-disable-next-line import/extensions -- directive added automatically by Shepherd migration
import { getInsuranceUUID } from '../lib/static-data-sdk.js';
import { checkRequiredURIParameters } from '../lib/log-shopping-finished-event';
import { isCCCardArtEligible } from '../lib/cc-card-art-utils';
import { isCredibleInterstitialArtEligible } from '../lib/credible-interstitial-utils';

import {
  fetchAndLog,
  setRedirectSuppressed,
  setRedirectTimeout,
  setComponentName,
  showMissingTrackingPrams,
  supressSplashPage,
} from '../lib/actions';

const baseUri = appConfig.BASE_URI;
const investingProvidersInMarketplace = Object.keys(investingOfferIdMapping);

const isSuppressed = (req) =>
  'suppress' in req.query || appConfig.SUPPRESS_REDIRECT;

const addAutomaticRedirectIfNeeded = ({
  store,
  req,
  res,
  url,
  timeout = REDIRECT_TIMEOUT,
}) => {
  const isForced = 'force' in req.query;
  let initiateRedirect = !isSuppressed(req) || isForced;

  if (appConfig.SHOULD_SHOW_MISSING_TRACKING_PARAMS) {
    const missingTrackingParams = checkRequiredURIParameters(req);
    if (missingTrackingParams.length > 0) {
      initiateRedirect = false;
      store.dispatch(showMissingTrackingPrams(missingTrackingParams));
    }
  }

  if (initiateRedirect) {
    const dynamicMeta = get(res, 'locals.nerdlayout.dynamicMeta', []);
    dynamicMeta.push({
      'http-equiv': 'refresh',
      content: `1; url=${url}`,
    });
    set(res, 'locals.nerdlayout.dynamicMeta', dynamicMeta);
    store.dispatch(setRedirectTimeout(timeout));
  }

  // We also need to tell the store if we're doing a redirect so that we can
  // display an appropriate message to the user
  store.dispatch(setRedirectSuppressed(!initiateRedirect));
};

const UNREPORTED_ERRORS = [
  NOTFOUND_ERROR_MSG,
  PREQUAL_ISSUER_NOTFOUND_ERROR_MSG,
];

const handleError = (err) => {
  if (!includes(UNREPORTED_ERRORS, err.message)) {
    throw err;
  }
};

/**
 * Test exception route (set to non-publically accessible in edge-configs)
 */
const testExceptionRoute = {
  path: `${baseUri}/exception-test`,
  loadData: async () => {
    throw new Error('Exception test');
  },
};

/**
 * App routes
 */
const creditCardPrequalRoute = {
  path: `${baseUri}/prequalify/credit-cards/:id`,
  loadData: async ({ store, res, req, nwa, match }) => {
    // Our regex path means we can't use named params for slug
    const { id } = match.params;
    const slug = 'credit-cards';
    const redirectMatch = {
      params: {
        id,
        slug,
      },
    };

    let data = {};
    try {
      data = await fetchPrequalRedirectData({
        store,
        res,
        req,
        nwa,
        match: redirectMatch,
      });
    } catch (e) {
      handleError(e);
      datadog.increment(
        'interstitial_client.fetch_redirect_details.marketplace.error',
        1,
        { tags: { redirect_type: 'prequal' } }
      );
      return;
    }

    const { clientData, loggerData } = data;
    // CC Prequal is always monetizing
    clientData.gtmEvent = 'conversion_cc_monetizing';
    await store.dispatch(
      fetchAndLog({
        req,
        clientData,
        loggerData,
      })
    );

    if (clientData.issuer in PREQUAL_SERVICE_ISSUERS) {
      const componentName = get(
        PREQUAL_SERVICE_ISSUERS,
        [clientData.issuer, 'componentName'],
        'default'
      );
      store.dispatch(setComponentName(componentName));
    } else {
      req.log.error({
        message: `${clientData.issuer} not a valid cc-prequal issuer.`,
        context: { product_type: 'credit_cards' },
      });
    }

    store.dispatch(setRedirectSuppressed(isSuppressed(req)));
  },
};

const creditCardRoute = {
  path: `${baseUri}/credit-cards/:id`,
  loadData: async ({ store, res, req, nwa, match }) => {
    // Our regex path means we can't use named params for id or slug
    const { id } = match.params;
    const slug = 'credit-cards';
    const redirectMatch = {
      params: {
        id,
        slug,
      },
    };

    let data = {};
    try {
      data = await fetchRedirectData({
        store,
        res,
        req,
        nwa,
        match: redirectMatch,
      });
    } catch (e) {
      handleError(e);
      datadog.increment(
        'interstitial_client.fetch_redirect_details.marketplace.error',
        1,
        { tags: { redirect_type: 'legacy' } }
      );
      return;
    }

    const { clientData, loggerData } = data;
    if (clientData.monetizing) {
      clientData.gtmEvent = 'conversion_cc_monetizing';
    }
    await store.dispatch(
      fetchAndLog({
        req,
        clientData,
        loggerData,
      })
    );

    store.dispatch(setComponentName('Default'));
    addAutomaticRedirectIfNeeded({
      store,
      req,
      res,
      url: clientData.destinationUrl,
    });
  },
};

const networkLinksInvestingProvidersRoute = {
  path: `${baseUri}/network-links/(${investingProvidersInMarketplace.join(
    '|'
  )})`,
  loadData: async ({ store, res, req, nwa, match }) => {
    // Our regex path means we can't use named params for id or slug
    const id = match.params[0];
    const extraData = investingOfferIdMapping[parseInt(id, 10)];
    if (!extraData) {
      return;
    }

    const slug = 'investing-offers';
    const redirectMatch = {
      params: {
        id: extraData.offerId,
        slug,
      },
    };

    let data = {};
    try {
      data = await fetchRedirectData({
        store,
        res,
        req,
        nwa,
        match: redirectMatch,
      });
    } catch (e) {
      handleError(e);
      datadog.increment(
        'interstitial_client.fetch_redirect_details.marketplace.error',
        1,
        { tags: { redirect_type: 'legacy' } }
      );
      return;
    }

    const { clientData, loggerData } = data;
    await store.dispatch(
      fetchAndLog({
        req,
        clientData,
        loggerData,
      })
    );

    store.dispatch(setComponentName('InvestingAdvisor'));
  },
};

const insuranceRoute = {
  path: `${baseUri}/insurance/:type/:id`,
  loadData: async ({ store, res, req, nwa, match }) => {
    const redirectMatch = {
      params: {
        uuid: getInsuranceUUID(match.params.id, match.params.type),
      },
    };

    let data = {};
    try {
      data = await fetchUuidRedirectData({
        store,
        res,
        req,
        nwa,
        match: redirectMatch,
      });
    } catch (e) {
      handleError(e);
      datadog.increment(
        'interstitial_client.fetch_redirect_details.marketplace.error',
        1,
        { redirect_type: 'uuid' }
      );
      return;
    }

    const { clientData, loggerData } = data;
    await store.dispatch(
      fetchAndLog({
        req,
        clientData,
        loggerData,
      })
    );

    store.dispatch(setComponentName('Default'));
    addAutomaticRedirectIfNeeded({
      store,
      req,
      res,
      url: clientData.destinationUrl,
    });
  },
};

const defaultRoute = {
  name: 'default',
  path: `${baseUri}/:slug/:id`,
  loadData: async ({ store, res, req, nwa, match }) => {
    let data = {};
    try {
      data = await fetchRedirectData({ store, res, req, nwa, match });
    } catch (e) {
      handleError(e);
      datadog.increment(
        'interstitial_client.fetch_redirect_details.marketplace.error',
        1,
        { redirect_type: 'legacy' }
      );
      return;
    }

    const { clientData, loggerData } = data;
    await store.dispatch(
      fetchAndLog({
        req,
        clientData,
        loggerData,
      })
    );

    const { productType, showOtherOptions, secName } = clientData;

    // The AdvisoryInterstitial is used to show a Form ADV disclosure and/or other investing options
    // The disclosure is shown only if secName exists (see AdvisoryInterstitial component)
    const shouldShowAdvisoryInterstitial =
      productType === 'Investment Advisor' && (showOtherOptions || secName);
    if (shouldShowAdvisoryInterstitial) {
      store.dispatch(setComponentName('InvestingAdvisor'));
    } else {
      store.dispatch(setComponentName('Default'));
      addAutomaticRedirectIfNeeded({
        store,
        req,
        res,
        url: clientData.destinationUrl,
      });
    }
  },
};

const uuidRoute = {
  path: `${baseUri}/:uuid`,
  loadData: async ({ store, res, req, nwa, match }) => {
    let data = {};
    try {
      data = await fetchUuidRedirectData({ store, res, req, nwa, match });
    } catch (e) {
      handleError(e);
      datadog.increment(
        'interstitial_client.fetch_redirect_details.marketplace.error',
        1,
        { redirect_type: 'uuid' }
      );
      return;
    }

    const shouldSupressPage = get(req, 'query.supressSplashPage') === 'true';
    if (shouldSupressPage) {
      store.dispatch(supressSplashPage());
    }

    const { clientData, loggerData } = data;
    await store.dispatch(
      fetchAndLog({
        req,
        clientData,
        loggerData,
      })
    );

    const {
      productType,
      showOtherOptions,
      secName,
      marketplaceOfferProductType,
    } = clientData;
    const { productName } = loggerData;

    // The AdvisoryInterstitial is used to show a Form ADV disclosure and/or other investing options
    // The disclosure is shown only if secName exists (see AdvisoryInterstitial component)
    const shouldShowAdvisoryInterstitial =
      (productType === 'Investment Advisor' &&
        (!!showOtherOptions || !!secName)) ||
      secName === 'Merrill Edge' ||
      productName === 'Merrill Edge IRA';

    if (shouldShowAdvisoryInterstitial) {
      store.dispatch(setComponentName('InvestingAdvisor'));
    } else if (isCCCardArtEligible(req, marketplaceOfferProductType)) {
      store.dispatch(setComponentName('CCCardArt'));
      addAutomaticRedirectIfNeeded({
        store,
        req,
        res,
        url: clientData.destinationUrl,
      });
    } else if (isCredibleInterstitialArtEligible(req)) {
      store.dispatch(setComponentName('CredibleInterstitialArt'));
      addAutomaticRedirectIfNeeded({
        store,
        req,
        res,
        url: clientData.destinationUrl,
      });
    } else {
      store.dispatch(setComponentName('Default'));
      addAutomaticRedirectIfNeeded({
        store,
        req,
        res,
        url: clientData.destinationUrl,
      });
    }
  },
};

export default [
  testExceptionRoute,
  creditCardPrequalRoute,
  creditCardRoute,
  networkLinksInvestingProvidersRoute,
  insuranceRoute,
  // IMPORTANT: the default route must come last as a catch-all
  defaultRoute,
  uuidRoute,
];

// We export each route individually for testing
export {
  testExceptionRoute,
  creditCardPrequalRoute,
  creditCardRoute,
  networkLinksInvestingProvidersRoute,
  insuranceRoute,
  defaultRoute,
  uuidRoute,
};
